import type { AirlineNameChangePolicyRequest, AirlineWriteRequest } from '@/api/generated/schemas';
import { AirlineCheckInType } from '@/api/generated/schemas';
import { airlineAmount, airlineInteger, type AirlineFormValues } from './airlineFormSchema';

/** Okno patří jen k online odbavení; jinak jde na drát `null` bez ohledu na obsah pole. */
function onlineWindowHours(values: AirlineFormValues, raw: string): number | null {
  return values.checkInType === AirlineCheckInType.Online ? airlineInteger(raw) : null;
}

/**
 * Podmínky změn jmen; `null` = dopravce je neumožňuje. Backend podle toho řádek založí, přepíše,
 * nebo i s vyplněnými hodnotami smaže — samostatný příznak povolení neexistuje.
 * <p>
 * Lhůty jdou na drát jen jako úplné dvojice: bez čísla se neposílá ani jednotka, i když ji
 * přepínač ve formuláři pořád nějakou drží.
 * </p>
 */
function toNameChangePolicy(values: AirlineFormValues): AirlineNameChangePolicyRequest | null {
  if (values.nameChanges !== 'Allowed') {
    return null;
  }

  const deadlineValue = airlineInteger(values.nameChangeDeadlineValue);
  const namelistValue = airlineInteger(values.nameChangeNamelistValue);

  return {
    deadlineValue,
    deadlineUnit: deadlineValue === null ? null : values.nameChangeDeadlineUnit,
    feeMode: values.nameChangeFeeMode,
    onlineFee: airlineAmount(values.nameChangeOnlineFee),
    airportFee: airlineAmount(values.nameChangeAirportFee),
    namelistValue,
    namelistUnit: namelistValue === null ? null : values.nameChangeNamelistUnit,
    note: emptyToNull(values.nameChangeNote),
  };
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Mapuje hodnoty formuláře na společné API tělo zápisu letecké společnosti (create i update mají
 * stejný tvar). Trim a velká písmena u kódů dělá i backend — tady jde jen o to, aby se neposílaly
 * prázdné řetězce místo „nevyplněno".
 */
export function toAirlineRequestBody(values: AirlineFormValues): AirlineWriteRequest {
  return {
    organizationPartyId: values.organization?.partyId ?? 0,
    tradeName: values.tradeName.trim(),
    iata: emptyToNull(values.iata)?.toUpperCase() ?? null,
    icao: emptyToNull(values.icao)?.toUpperCase() ?? null,
    callsign: emptyToNull(values.callsign),
    web: emptyToNull(values.web),
    currencyId: values.currency?.id ?? null,
    status: values.status,
    note: emptyToNull(values.note),
    ticketTypes: values.ticketTypes,
    allowsOneWay: values.purchaseStrategies.includes('OneWay'),
    allowsRoundTrip: values.purchaseStrategies.includes('RoundTrip'),
    allowsMulticity: values.purchaseStrategies.includes('Multicity'),
    checkInType: values.checkInType,
    checkInWindowFromHours: onlineWindowHours(values, values.checkInWindowFromHours),
    checkInWindowToHours: onlineWindowHours(values, values.checkInWindowToHours),
    nameChangePolicy: toNameChangePolicy(values),
  } satisfies AirlineWriteRequest;
}
